// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyClient } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    client: TyClient;
    onMore?: (e: React.MouseEvent<HTMLButtonElement>) => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function ClientCard({ client, onMore }: Props) {
    const t = useTranslation();

    const access = client.storeAccess;
    const uniqueModels = [...new Set((access?.permissions ?? []).map((p) => p.model))];

    const modelLabels: Record<string, string> = {
        menu: t.sideBar.menu,
        branches: t.sideBar.branches,
        clients: t.sideBar.clients,
        subscriptions: t.sideBar.subscriptions,
        settings: t.sideBar.settings,
    };

    const hasAllBranches = !access || access.branches === "all";
    const uniqueBranches = hasAllBranches ? [] : (access.branches as { _id: string; name: string }[]).filter((b) => b.name);

    const hasBadges = client.isOwner || uniqueModels.length > 0;
    const hasBranches = !client.isOwner && (hasAllBranches || uniqueBranches.length > 0);

    return (
        <div className="client-card">
            {/* --- Header: avatar + identity + more btn -------------------------------------- */}
            <div className="client-card-header">
                <div className="client-card-avatar">
                    {client.picture ? (
                        <img src={client.picture} alt="" className="client-card-avatar-img" />
                    ) : (
                        <div className="client-card-avatar-placeholder">
                            <img className="client-card-avatar-icon" src="/icons/client-icon.svg" alt="" width={20} height={20} />
                        </div>
                    )}
                </div>
                <div className="client-card-identity">
                    <p className="client-card-name">{client.name}</p>
                    <p className="client-card-phone">{client.phone}</p>
                </div>
                {onMore && (
                    <button
                        type="button"
                        className="client-card-more-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            onMore(e);
                        }}
                    >
                        <img src="/icons/more-icon.svg" alt="" width={16} height={16} />
                    </button>
                )}
            </div>
            {/* --- Footer: access badges + branch badges -------------------------------------- */}
            {(hasBadges || hasBranches) && (
                <div className="client-card-footer">
                    {hasBadges && (
                        <div className="client-card-footer-row">
                            {client.isOwner && (
                                <div className="client-card-owner-badge">
                                    <span>{t.clients.ownerBadge}</span>
                                </div>
                            )}
                            {uniqueModels.map((model) => (
                                <div key={model} className="client-card-model-badge">
                                    <span>{modelLabels[model] ?? model}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {hasBranches && (
                        <div className="client-card-footer-row">
                            {hasAllBranches ? (
                                <div className="client-card-branch-badge">
                                    <span>{t.clients.allBranches}</span>
                                </div>
                            ) : (
                                uniqueBranches.map((b) => (
                                    <div key={b._id} className="client-card-branch-badge">
                                        <span>{b.name}</span>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
